import './App.css';
import { Amplify } from 'aws-amplify';
import { useAuthenticator, Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';

import routes from 'routes';
import { useAppController, setDarkMode } from 'context';

import brandWhite from 'assets/images/homegrid-logo-white.png';
import brandDark from 'assets/images/HomeGrid-logo.png';
import brandMiniWhite from 'assets/images/hg-logo-mini-white.png';
import brandMiniDark from 'assets/images/hg-logo-mini.png';

import awsConfig from './aws-config';
import AppLayout from 'components/AppLayout';

import HGAuthenticator from 'components/HGAuthenticator';
import SyncProInstallation from 'components/SyncProInstallation';
import { ProtectedRoute } from 'components/ProtectedRoute';
import { NotFoundPage } from 'routes';
import {
    useVerifyAdmin,
    useVerifyCompanyAdmin,
    useVerifyInstaller,
    useSitesPermission,
} from 'helper';
import { Toaster } from 'components/shadcn/ui/toaster';
import { useInactivityTimeout } from 'hooks/useInactivityTimeout';
import { SessionExpiryModal } from 'components/SessionExpiryModal';

Amplify.configure(awsConfig);

function App() {
    const { authStatus, signOut } = useAuthenticator((context) => [context.user]);
    const [controller, dispatch] = useAppController();
    const { darkMode } = controller;
    const { pathname } = useLocation();
    const isAdmin = useVerifyAdmin();
    const isCompanyAdmin = useVerifyCompanyAdmin();
    const canManageSites = useSitesPermission();
    const isInstaller = useVerifyInstaller();
    const { showWarning, resetTimer } = useInactivityTimeout();

    useEffect(() => {
        document.documentElement.classList.toggle('dark', darkMode);
    }, [darkMode]);

    useEffect(() => {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e) => {
            if (localStorage.getItem('hg-dark-mode') === null) {
                setDarkMode(dispatch, e.matches);
            }
        };
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, [dispatch]);

    useEffect(() => {
        document.documentElement.scrollTop = 0;
        document.scrollingElement.scrollTop = 0;
    }, [pathname]);

    const visibleRoutes = routes.filter((route) => {
        if (route.requireAdmin && !isAdmin) return false;
        if (route.requireCompanyAdmin && !isCompanyAdmin && !isAdmin) return false;
        if (route.hideForCompanyAdmin && !isAdmin) return false;
        if (route.requireInstaller && !canManageSites) return false;
        if (route.hideForInstaller && !isAdmin && !isCompanyAdmin) return false;

        if (route.collapse) {
            route.collapse = route.collapse.filter((nestedRoute) => {
                if (nestedRoute.hidden) return false;
                if (nestedRoute.requireAdmin && !isAdmin) return false;
                if (nestedRoute.requireCompanyAdmin && !isCompanyAdmin && !isAdmin) return false;
                if (nestedRoute.hideForCompanyAdmin && isCompanyAdmin) return false;
                if (nestedRoute.requireInstaller && !canManageSites) return false;
                if (route.hideForInstaller && isInstaller && !isAdmin) return false;
                return true;
            });
            return true;
        }

        return !route.hidden;
    });

    const getRoutes = (allRoutes) =>
        allRoutes.flatMap((route) => {
            if (route.collapse) return [...getRoutes(route.collapse)];
            if (route.requireAdmin && !isAdmin) return [];
            if (route.requireCompanyAdmin && !isCompanyAdmin && !isAdmin) return [];
            if (route.hideForCompanyAdmin && !isAdmin) return [];
            if (route.requireInstaller && !canManageSites) return [];
            if (route.hideForInstaller && !isAdmin && !isCompanyAdmin) return [];

            if (route.route) {
                return (
                    <Route
                        exact
                        path={route.route}
                        element={
                            <ProtectedRoute>
                                <AppLayout
                                    routes={visibleRoutes}
                                    brand={darkMode ? brandWhite : brandDark}
                                    brandMini={darkMode ? brandMiniWhite : brandMiniDark}
                                >
                                    {route.component}
                                </AppLayout>
                            </ProtectedRoute>
                        }
                        key={route.key}
                    />
                );
            }
            return [];
        });

    return (
        <>
            <Routes>
                {getRoutes(routes)}
                <Route
                    path="/login"
                    element={
                        authStatus === 'authenticated' ? (
                            <Navigate to="/profile" />
                        ) : authStatus === 'unauthenticated' ? (
                            <HGAuthenticator />
                        ) : (
                            <div className="flex h-screen items-center justify-center">
                                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
                            </div>
                        )
                    }
                />
                <Route path="/sync-pro-installation" element={<SyncProInstallation />} />
                <Route path="*" element={<NotFoundPage />} />
            </Routes>
            <SessionExpiryModal
                open={showWarning}
                onStaySignedIn={resetTimer}
                onSignOut={() => { sessionStorage.clear(); signOut(); }}
            />
            <Toaster />
        </>
    );
}

const reactQueryClient = new QueryClient();
export default () => {
    return (
        <QueryClientProvider client={reactQueryClient}>
            <Authenticator.Provider>
                <App />
            </Authenticator.Provider>
        </QueryClientProvider>
    );
};
